import type {
  BulkWeightValue,
  KindCondition,
  ProductThreshold,
  SpecialRules,
  SpecialRuleSection,
  SpecialRuleSetFor,
  SpecialRuleSetsFor,
  SpecialType,
  SpecialTypeWithRules,
} from '@iheartjane/types';
import compact from 'lodash-es/compact';
import isEmpty from 'lodash-es/isEmpty';

import type {
  ConditionMultiselectRangeValue,
  ConditionRangeValue,
  ConditionValue,
  ConditionSelection,
  ConditionSet,
} from '../conditions.types';
import { decodeKey, encodeKey } from './conditionKeys';
import {
  CONDITION_SELECTION_TYPES,
  isRangeValue,
  parseUnitValueKey,
  unitValueKey,
} from './conditionSelection.util';
import {
  dropConflictingExclusions,
  foldExclusions,
  getExclusionOverlaps,
} from './foldRedundantExclusions';
import { updateConditionSelection } from './updateConditionSelection';

export const EMPTY_CONDITION_SET = [
  {
    applyTo: 'attributes',
    selections: {
      excludes: {},
      includes: {},
    },
  },
];

/** Any rule set family — only these keys are read, whatever the rules hold */
type RuleSetLike = {
  excludes?: object;
  includes?: object;
  product_threshold?: ProductThreshold;
};

/** The editor key a price range is selected under */
const PRICE_KEY = 'price';

const toRange = ({
  maximum_price,
  minimum_price,
}: ProductThreshold): ConditionRangeValue => ({
  ...(maximum_price != null && { max: maximum_price }),
  ...(minimum_price != null && { min: minimum_price }),
});

/** Inverse of {@link toRange}. An open bound is left out rather than sent null */
const toBounds = ({ max, min }: ConditionRangeValue): ProductThreshold => ({
  ...(max !== undefined && { maximum_price: max }),
  ...(min !== undefined && { minimum_price: min }),
});

/** Rule keys whose stored shape differs from what the editor selects on */
const TRANSFORMS: Record<string, (value: unknown) => ConditionValue> = {
  kinds: (value) =>
    (value as KindCondition[]).map(({ brand_subtype, kind, root_subtype }) =>
      encodeKey(compact([kind, root_subtype, brand_subtype]))
    ),
  // ids arrive as numbers; the editor selects on strings
  brand_ids: (value) => (value as number[]).map(String),
  product_ids: (value) => (value as number[]).map(String),
  // one range per weight, keyed the way the editor's options are
  bulk_weight_values: (value) =>
    Object.fromEntries(
      (value as BulkWeightValue[]).map((weight) => [
        unitValueKey(weight.weight_value),
        toRange(weight),
      ])
    ),
};

/** Rule keys the editor names differently */
const KEY_MAPPING: Record<string, string> = {
  bulk_weight_values: 'weight_and_price',
};

const toConditionValue = (key: string, value: unknown): ConditionValue =>
  TRANSFORMS[key] ? TRANSFORMS[key](value) : (value as ConditionValue);

const toConditionSelection = (ruleSet: RuleSetLike): ConditionSelection => {
  const selection: ConditionSelection = { excludes: {}, includes: {} };

  CONDITION_SELECTION_TYPES.forEach((selectionType) => {
    const rules: Record<string, unknown> = { ...ruleSet[selectionType] };

    // rule type keys (brand_ids, product_ids, lineages)
    Object.entries(rules).forEach(([key, value]) => {
      selection[selectionType] = updateConditionSelection(
        selection,
        selectionType,
        KEY_MAPPING[key] ?? key,
        toConditionValue(key, value)
      )[selectionType];
    });
  });

  if (ruleSet.product_threshold) {
    selection.includes[PRICE_KEY] = toRange(ruleSet.product_threshold);
  }

  return selection;
};

export const getConditionSelections = <R extends RuleSetLike>(
  ruleSets: ['any', R, ...R[]]
): ConditionSelection[] => {
  const [, ...entries] = ruleSets;

  return entries.map(toConditionSelection);
};

/**
 * Legacy rules could include and exclude the same brands, categories, and so
 * on, but a section is included or excluded, never both — editing one side
 * clears the other — so each entry is reduced to inclusions alone. Where the
 * exclusions rule out every inclusion, the inclusions win: the narrower of the
 * two readings a section can hold.
 */
const simplifyExclusions = (ruleSet: RuleSetLike): RuleSetLike => {
  const { excludes, includes } = ruleSet;
  if (!excludes || !includes) return ruleSet;

  const folded = foldExclusions(
    includes,
    dropConflictingExclusions(includes, excludes)
  );

  return { ...ruleSet, excludes: folded.exclude, includes: folded.include };
};

const getApplyTo = (selection: ConditionSelection) => {
  if (selection.includes['product_ids']) {
    return 'products';
  }
  return 'attributes';
};

/**
 * Converts from special's rule_sets payload (after the "any") to the
 * ConditionSet which we need for the conditions components.
 */
export const convertRuleSetsToConditionSets = <R extends RuleSetLike>(
  ruleSets?: ['any', R, ...R[]]
): ConditionSet[] => {
  if (!ruleSets) {
    return EMPTY_CONDITION_SET;
  }

  const [, ...entries] = ruleSets;

  return entries.map((ruleSet) => {
    const overlaps = getExclusionOverlaps(ruleSet.includes, ruleSet.excludes);
    const selections = toConditionSelection(simplifyExclusions(ruleSet));

    return {
      applyTo: getApplyTo(selections),
      ...(!isEmpty(overlaps) && { overlaps }),
      selections,
    };
  });
};

/** Rule keys the editor selects on in a shape the special does not store */
const REVERSE_TRANSFORMS: Record<string, (value: ConditionValue) => unknown> = {
  kinds: (value) =>
    (value as string[]).map((key) => {
      const [kind, root_subtype, brand_subtype] = decodeKey(key);

      return {
        kind,
        ...(root_subtype && { root_subtype }),
        ...(brand_subtype && { brand_subtype }),
      };
    }),
  // the editor selects on strings; the special stores ids
  brand_ids: (value) => (value as string[]).map(Number),
  product_ids: (value) => (value as string[]).map(Number),
  // one range per weight becomes one stored weight per priced row
  weight_and_price: (value) =>
    Object.entries(value as ConditionMultiselectRangeValue).flatMap(
      ([key, range]) => {
        const weightValue = parseUnitValueKey(key);

        return weightValue
          ? [{ weight_value: weightValue, ...toBounds(range) }]
          : [];
      }
    ),
};

/** Inverse of {@link KEY_MAPPING} */
const REVERSE_KEY_MAPPING: Record<string, string> = {
  weight_and_price: 'bulk_weight_values',
};

/**
 * One selection type's rules. `price` is left out: it is stored on the entry as
 * `product_threshold` rather than inside either side.
 */
const toRuleSetSide = (
  values: Record<string, ConditionValue>
): Record<string, unknown> =>
  Object.entries(values).reduce<Record<string, unknown>>(
    (side, [key, value]) => {
      if (key === PRICE_KEY) {
        return side;
      }

      const converted = REVERSE_TRANSFORMS[key]?.(value) ?? value;

      // a row the editor could not convert leaves nothing to store
      return Array.isArray(converted) && converted.length === 0
        ? side
        : { ...side, [REVERSE_KEY_MAPPING[key] ?? key]: converted };
    },
    {}
  );

const toProductThreshold = ({
  includes,
}: ConditionSelection): ProductThreshold | undefined => {
  const price = includes[PRICE_KEY];

  return isRangeValue(price) && (price.max ?? price.min) !== undefined
    ? toBounds(price)
    : undefined;
};

/**
 * One group as a rule set entry. Which keys a group can hold is decided by the
 * section definitions the editor rendered it with, so the sides are assembled
 * as plain maps and narrowed once here rather than built a typed key at a time.
 *
 * A group with nothing selected returns undefined, to be compatible with combining
 * with another condition group.
 */
const toRuleSetEntry = ({
  selections,
}: ConditionSet): SpecialRuleSetFor<SpecialTypeWithRules> | undefined => {
  const includes = toRuleSetSide(selections.includes);
  const excludes = toRuleSetSide(selections.excludes);
  const productThreshold = toProductThreshold(selections);

  const entry: SpecialRuleSetFor<SpecialTypeWithRules> = {
    ...(Object.keys(includes).length > 0 && { includes }),
    ...(Object.keys(excludes).length > 0 && { excludes }),
    ...(productThreshold && { product_threshold: productThreshold }),
  };

  return Object.keys(entry).length > 0 ? entry : undefined;
};

/**
 * Converts the editor's groups back into a special's `rule_sets` payload — the
 * inverse of {@link convertRuleSetsToConditionSets}.
 *
 * `rule_sets` must carry at least one entry, so an editor whose every group was
 * deleted saves as "matches everything" rather than as nothing at all.
 */
export const convertConditionSetsToRuleSets = (
  conditionSets: ConditionSet[]
): SpecialRuleSetsFor<SpecialTypeWithRules> => {
  const [first = { all_products: true }, ...rest] = compact(
    conditionSets.map(toRuleSetEntry)
  );
  return ['any', first, ...rest];
};

/** The rules key an editor's conditions are read from and saved back to */
export type ConditionEditorKey = 'default' | 'discountable' | 'qualifier';

/** One conditions editor: the heading it renders under and the key it saves to */
export type ConditionEditor = {
  conditionFor: string;
  rulesKey: ConditionEditorKey;
};

/** The rule sets one conditions editor owns */
export type LabeledRuleSets = ConditionEditor & {
  ruleSets?: SpecialRuleSetsFor<SpecialTypeWithRules>;
};

// qualifier/discountable hold one section (spending_threshold) or an array of
// them (bundle), so coerce before reading the rule sets
const sectionRuleSets = (
  section?: SpecialRuleSection | SpecialRuleSection[] | null
) => (Array.isArray(section) ? section[0] : section)?.rule_sets;

/**
 * The editors a special needs — two for the types with required and discounted
 * rule_sets, one otherwise. Only the type decides this, so the editors can be
 * rendered before the special's rules have landed.
 */
export const getConditionEditors = (
  specialType?: SpecialType | ''
): ConditionEditor[] => {
  if (specialType === 'bundle' || specialType === 'spending_threshold') {
    return [
      { conditionFor: 'required products', rulesKey: 'qualifier' },
      { conditionFor: 'discounted products', rulesKey: 'discountable' },
    ];
  }

  return [
    {
      conditionFor: specialType
        ? `${specialType.split('_').join(' ')} deal`
        : '',
      rulesKey: 'default',
    },
  ];
};

/** Where each editor's rule sets are read from within a special's rules */
const RULE_SETS_SOURCES: Record<
  ConditionEditorKey,
  (rules: SpecialRules) => SpecialRuleSetsFor<SpecialTypeWithRules> | undefined
> = {
  default: (rules) => rules.rule_sets,
  discountable: (rules) => sectionRuleSets(rules.discountable),
  qualifier: (rules) => sectionRuleSets(rules.qualifier),
};

/** Splits a special's rules into the editors it needs, each with its own sets */
export const getLabeledRuleSets = (
  rules: SpecialRules,
  specialType?: SpecialType | ''
): LabeledRuleSets[] =>
  getConditionEditors(specialType).map((editor) => ({
    ...editor,
    ruleSets: RULE_SETS_SOURCES[editor.rulesKey](rules),
  }));

/** Every editor's groups, keyed by the rules key that editor saves to */
export type ConditionSetsByKey = Record<ConditionEditorKey, ConditionSet[]>;

/**
 * The starting groups for every editor a special could need. Keys this special
 * type has no editor for are seeded too, so changing the type mid-form finds an
 * empty editor waiting rather than nothing at all.
 */
export const convertRulesToConditionSets = (
  rules: SpecialRules = {},
  specialType?: SpecialType | ''
): ConditionSetsByKey => {
  const seeded = Object.fromEntries(
    getLabeledRuleSets(rules, specialType).map(({ rulesKey, ruleSets }) => [
      rulesKey,
      convertRuleSetsToConditionSets(ruleSets),
    ])
  );

  return {
    default: seeded['default'] ?? EMPTY_CONDITION_SET,
    discountable: seeded['discountable'] ?? EMPTY_CONDITION_SET,
    qualifier: seeded['qualifier'] ?? EMPTY_CONDITION_SET,
  };
};
